/**
 * API response shapes shared by the workers and the three frontends.
 * Request bodies live in schemas.ts (zod), so both sides validate the same way.
 *
 * Every frontend calls its own origin under `/api/...`; the edge worker of that app forwards to the
 * backend worker. Errors always come back as `ApiErrorBody` with a 4xx/5xx status.
 */
import type { Lang, LocalizedText } from './i18n.ts';
import type { AdminRole, AutomationSettings, BrandEntitlements, GiftCardProductPublic, MemberPaidMembership, PaidPlanPublic, ReferralSettings, StoredValueSettings } from './growth.ts';
import type { AppliedCampaign, CampaignPublic } from './campaigns.ts';
import type { PosSettings } from './pos.ts';

export type ErrorCode =
  | 'VALIDATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'EMAIL_TAKEN'
  | 'INVALID_CREDENTIALS'
  | 'OTP_REQUIRED'
  | 'CODE_INVALID'
  | 'CODE_EXPIRED'
  | 'BRAND_NOT_FOUND'
  | 'BRAND_SUSPENDED'
  | 'POS_UNAVAILABLE'
  | 'POS_REJECTED'
  | 'INSUFFICIENT_POINTS'
  | 'NOT_IMPLEMENTED'
  /** A plan limit (staff, shops, SMS) is reached. */
  | 'LIMIT_REACHED'
  /** The brand's plan does not include this module. */
  | 'MODULE_OFF'
  /** SMS login is not available now (gateway off, quota or daily cap reached); use email instead. */
  | 'SMS_UNAVAILABLE'
  /** AI is switched off, not set up, or the AI service failed; try again later. */
  | 'AI_UNAVAILABLE'
  /** The AI service refused the request (e.g. its content check); change the description. */
  | 'AI_REJECTED'
  /** The email service did not take a 電子報 test email; try again later. */
  | 'EMAIL_FAILED'
  | 'INTERNAL';

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; fields?: Record<string, string> };
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type QrMode = 'fixed' | 'dynamic';

/**
 * Where a brand's members, points, levels and coupons live, chosen when the brand is created and
 * never changed: `proan` = the brand's PROAN POS is the source of truth; `native` = this platform's
 * own loyalty engine (for merchants without a PROAN POS).
 */
export type DataSource = 'proan' | 'native';

/**
 * Marketing channels a member agrees to separately (PDPO direct marketing). Each has its own
 * yes/no and time; anything sent for marketing checks the channel first.
 */
export const CONSENT_CHANNELS = ['email', 'sms', 'whatsapp', 'wallet'] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];
export type MemberConsents = Record<ConsentChannel, { granted: boolean; at: string | null }>;
/** Member-site templates: only the presentation differs, every page and feature works in each. */
export const SITE_TEMPLATES = ['classic', 'trendy', 'modern', 'noir', 'pop'] as const;
export type SiteTemplate = (typeof SITE_TEMPLATES)[number];
export type Gender = 'M' | 'F';
export type FieldRule = 'hidden' | 'optional' | 'required';

export interface ThemeColors {
  /** Main brand colour: header, buttons. Hex like #0F766E. */
  primary: string;
  /** Second colour: secondary buttons, highlights. */
  secondary: string;
  /** Accent: badges, points, small highlights. */
  accent: string;
}

/**
 * Each value is either the account name (e.g. "mtrhk") or, for older settings, a full URL.
 * Build the link with `socialUrl()` (social.ts).
 */
export interface SocialLinks {
  facebook?: string;
  instagram?: string;
  whatsapp?: string;
  youtube?: string;
  threads?: string;
  xiaohongshu?: string;
  linkedin?: string;
  website?: string;
}

export interface StoreInfo {
  /** Stable id given by the server (sales, staff and promotions refer to it). Absent only before the first save. */
  id?: string;
  name: LocalizedText;
  address: LocalizedText;
  phone?: string;
  hours: LocalizedText;
  /** Google Maps share link; when set the address links to it. */
  mapUrl?: string;
  /** shopCode the POS sends for this shop (POS 連接). */
  posCode?: string;
  /** Where the shop is (WGS84), for Wallet 附近通知; null = not set. */
  location?: { lat: number; lng: number } | null;
}

// ---------------------------------------------------------------- member site (public)

/** Blocks of the member-site home page that the brand can show, hide and reorder (網站設定 › 主頁內容). */
export const HOME_SECTIONS = ['banner', 'promotions', 'about', 'levels', 'news', 'join'] as const;
export type HomeSectionKey = (typeof HOME_SECTIONS)[number];

/** Each template's own order of the home blocks, used until the brand sets one. */
export const TEMPLATE_HOME_ORDER: Record<SiteTemplate, HomeSectionKey[]> = {
  classic: ['banner', 'promotions', 'about', 'levels', 'news', 'join'],
  trendy: ['banner', 'promotions', 'levels', 'about', 'news', 'join'],
  modern: ['banner', 'promotions', 'about', 'levels', 'news', 'join'],
  noir: ['banner', 'promotions', 'about', 'levels', 'news', 'join'],
  pop: ['banner', 'promotions', 'levels', 'about', 'news', 'join'],
};

export interface HomeSettings {
  /** The brand's order and on/off of the home blocks; null = the template's own order, all on. */
  sections: Array<{ key: HomeSectionKey; on: boolean }> | null;
  /** Seconds each 主頁橫額 slide shows before the carousel moves on. */
  bannerSeconds: number;
}

/**
 * Every home block in display order with its on/off. Blocks missing from a saved list (added to
 * the product later) are appended, switched on.
 */
export function homeSectionList(home: Pick<HomeSettings, 'sections'> | null | undefined, template: SiteTemplate): Array<{ key: HomeSectionKey; on: boolean }> {
  const saved = (home?.sections ?? []).filter((s, i, all) => HOME_SECTIONS.includes(s.key) && all.findIndex((x) => x.key === s.key) === i);
  const rest = (TEMPLATE_HOME_ORDER[template] ?? TEMPLATE_HOME_ORDER.classic).filter((k) => !saved.some((s) => s.key === k));
  return [...saved, ...rest.map((key) => ({ key, on: true }))];
}

export interface BannerPublic {
  id: string;
  imageUrl: string;
  mobileImageUrl: string | null;
  title: LocalizedText;
  link: string | null;
}

export interface NewsSummary {
  id: string;
  categoryId: string | null;
  title: LocalizedText;
  summary: LocalizedText;
  imageUrl: string | null;
  publishAt: string;
  featured: boolean;
}

export interface NewsDetail extends NewsSummary {
  /** Sanitised HTML per language. */
  body: LocalizedText;
}

export interface NewsCategoryPublic {
  id: string;
  name: LocalizedText;
}

/** "$amount = stamps" — e.g. { amount: 100, stamps: 1 } is $100 = 1 stamp. */
export interface StampRatio {
  amount: number;
  stamps: number;
}

/** "$amount = points" — e.g. { amount: 1, points: 1 } is $1 = 1 point. */
export interface EarnRatio {
  amount: number;
  points: number;
}

/** Upgrade / keep rules of one level in the platform's own programme (native brands). */
export interface LevelRules {
  /** Spend needed while on the level below to move up to this level; null = cannot be reached by spending. */
  upgradeSpend: number | null;
  /** Spend needed while on this level, before it expires, to keep it; null = kept without spending. */
  retainSpend: number | null;
  /** Months the level lasts after it is reached; null = until the end of the programme year. */
  validityMonths: number | null;
  /** Level reached next by spending (the "普通會員 → VIP" relation); null = top level. */
  upgradeToId: number | null;
  /** Level the member drops to when the retain spend is not met; null = the entry level. */
  downgradeToId: number | null;
  earn: EarnRatio;
  /** The brand's one stamp ratio (印花 page, the same for every level); null = stamps off or no ratio set. */
  stamps: StampRatio | null;
}

export interface LevelPublic {
  /** POS customer type id (PROAN brands) or the platform's level id (native brands). */
  custTypeId: number;
  name: LocalizedText;
  color: string;
  /** Description / benefits text. */
  benefits: LocalizedText;
  /** Member card background for this level. */
  cardImageUrl: string | null;
  iconUrl: string | null;
  /** Native brands only; PROAN brands keep their rules in the POS. */
  rules: LevelRules | null;
}

/** A reward on the stamp card: given automatically once the card reaches `stamps` (the stamps are kept). */
export interface StampRewardPublic {
  id: string;
  /** Stamps on the card needed for this reward. */
  stamps: number;
  title: LocalizedText;
  /** Coupon the member receives, when the gift is a coupon. */
  couponTitle: LocalizedText | null;
  /** Extra points the member receives. */
  points: number;
}

/** LPSS-35: a published stamp card that has not expired (a brand can have several; each sale adds stamps to every one). */
export interface StampCardPublic {
  id: string;
  /** Last day the card works (有效日期, Hong Kong); null = it does not expire. */
  validTo: string | null;
  rewards: StampRewardPublic[];
}

/** The membership programme page (GET /api/public/program). */
export interface ProgramPublic {
  dataSource: DataSource;
  levels: LevelPublic[];
  /** Native only: programme year, "MM-DD". */
  yearStart: string | null;
  yearEnd: string | null;
  /** Native only: every year on this "MM-DD" all points are cleared; null = points do not expire. */
  pointsClearDate: string | null;
  stamps: { enabled: boolean; clearDate: string | null; cards: StampCardPublic[]; ratio: StampRatio | null };
  welcomeGift: { points: number; couponTitle: LocalizedText | null } | null;
  birthdayGift: { points: number; couponTitle: LocalizedText | null; timing: 'day' | 'month' } | null;
  /** 推廣活動 running now or starting within 30 days (pointsPromotions module). */
  promotions: CampaignPublic[];
  /** 付費會籍 on sale at the counter (paidMembership module), else []. */
  paidPlans: PaidPlanPublic[];
  /** 電子禮券 on sale at the counter (giftCards module), else []. */
  giftCards: GiftCardProductPublic[];
  /** 儲值 rules (storedValue module), else null. */
  storedValue: Pick<StoredValueSettings, 'bonusRules' | 'minTopup' | 'maxBalance' | 'terms'> | null;
}

export interface SiteConfig {
  brand: { id: string; name: LocalizedText; logoUrl: string | null; faviconUrl: string | null; dataSource: DataSource };
  template: SiteTemplate;
  theme: ThemeColors;
  languages: Lang[];
  defaultLang: Lang;
  about: LocalizedText;
  /** Picture beside the brand story ("品牌故事"); null = the template's own artwork. */
  aboutImageUrl: string | null;
  /** Up to 10 brand photos shown under the description on the about page. */
  gallery: string[];
  contact: {
    address: LocalizedText;
    /** Google Maps share link for the main address. */
    mapUrl: string | null;
    officeHours: LocalizedText;
    phone: string;
    email: string;
    socials: SocialLinks;
    stores: StoreInfo[];
  };
  banners: BannerPublic[];
  /** Home page blocks and the banner carousel speed. */
  home: HomeSettings;
  promotions: NewsSummary[];
  latestNews: NewsSummary[];
  newsCategories: NewsCategoryPublic[];
  levels: LevelPublic[];
  legal: { terms: LocalizedText; privacy: LocalizedText; termsVersion: string };
  registration: {
    gender: FieldRule;
    phone: FieldRule;
    birthday: FieldRule;
    area: FieldRule;
    areaOptions: Array<{ value: string; label: LocalizedText }>;
    /** Brand switch for sign-up / log-in by SMS code; see features.phoneLogin for whether it works now. */
    phoneLogin: boolean;
    /** Brand switch for 以 Google 帳戶登入 (on unless the brand turned it off); see features.googleLogin. */
    googleLogin: boolean;
  };
  seo: { title: LocalizedText; description: LocalizedText; ogImageUrl: string | null };
  /**
   * wallet = the brand turned it on and the platform has Apple and/or Google credentials.
   * stamps = native brand with stamps switched on in the programme settings.
   */
  features: {
    news: boolean;
    rewards: boolean;
    wallet: boolean;
    stamps: boolean;
    /** SMS sign-up / log-in can be used now (brand switch + module + platform gateway). */
    phoneLogin: boolean;
    /** 以 Google 帳戶登入 / 登記 can be used now (brand switch + the platform's Google client). */
    googleLogin: boolean;
    /** 推薦朋友 page (referralRewards module, switched on in the brand portal). */
    referrals: boolean;
    /** 輸入優惠碼 on 我的優惠券 (codeBatches module). */
    codeClaim: boolean;
    storedValue: boolean;
    giftCards: boolean;
    paidMembership: boolean;
  };
  /** Cloudflare Turnstile site key; when set, register / login / forgot password / SMS code requests need a token. */
  turnstileSiteKey: string | null;
  /** Which wallets can issue cards right now. */
  wallets: { apple: boolean; google: boolean };
  qrMode: QrMode;
  /** Marketing channels this brand asks consent for, and its direct-marketing statement. */
  consent: { channels: ConsentChannel[]; statement: LocalizedText };
  /** Set while this browser is showing the brand portal's 預覽 (unsaved settings): when it ends. */
  previewUntil?: string;
  /**
   * With previewUntil: how long the preview has left, counted on the server when this config was made. The page
   * ends the preview after this long on the browser's own timer, so changing the computer clock adds no time (LPSS-28).
   */
  previewEndsInMs?: number;
}

// ---------------------------------------------------------------- member site (signed in)

export interface MemberSelf {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCc: string | null;
  phone: string | null;
  /** YYYY-MM-DD */
  birthday: string | null;
  gender: Gender | null;
  area: string | null;
  lang: Lang;
  marketingOptIn: boolean;
  /** POS member number (cust_code). */
  memberNo: string;
  createdAt: string;
  deletionRequested: boolean;
  consents: MemberConsents;
  /** Terms version the member last agreed to. */
  termsVersion: string | null;
  /** The phone number was proven with an SMS code (needed for SMS log-in). */
  phoneVerified: boolean;
  /** false = signed up by SMS without a password (can set one with forgot password). */
  hasPassword: boolean;
}

export interface QrPayload {
  mode: QrMode;
  value: string;
  /** When a dynamic code stops working; null for fixed codes. ISO time. */
  refreshAt: string | null;
}

export interface PosStatus {
  points: number;
  levelId: number;
  /** Level name as typed in the POS. */
  levelPosName: string;
  /** Display settings for this level from the brand portal, when set. */
  level: LevelPublic | null;
  nextLevel: {
    level: LevelPublic | null;
    posName: string | null;
    /** Spend still needed to upgrade (HKD). */
    remainingAmount: number;
    /** Spend counted so far towards the upgrade. */
    accumulatedAmount: number;
    /** Days left to spend `remainingAmount`, when the POS gives one. */
    withinDays: number | null;
  } | null;
  levelStart: string | null;
  levelEnd: string | null;
  /** Last day current points are valid, from the POS points reset date. */
  pointsExpiry: string | null;
}

/**
 * The member's stamps on one stamp card. Rewards are given automatically as the card reaches each count; when it
 * reaches the highest one the card is complete and a new card starts with the stamps left over.
 */
export interface StampStatus extends StampCardPublic {
  /** Stamps on the current card. */
  count: number;
  /** Last day current stamps are valid; null = they do not expire. */
  expiry: string | null;
  /** Ids of the rewards already given on the current card. */
  received: string[];
  ratio: StampRatio | null;
}

export interface MemberDashboard {
  member: MemberSelf;
  dataSource: DataSource;
  /**
   * Points and level. PROAN brands: from the POS, null when it could not be reached (the page should
   * still render). Native brands: from the platform's own engine, always present.
   */
  pos: PosStatus | null;
  /** Native brands with stamps switched on: each published card that has not expired; otherwise null. */
  stamps: StampStatus[] | null;
  qr: QrPayload;
  /** Set when the brand published new terms the member has not agreed to yet; show them before anything else. */
  termsUpdate: { version: string } | null;
  /** Wallet cards this member already added. */
  walletAdded: { apple: boolean; google: boolean };
  /** 訊息中心 messages not read yet. */
  unreadMessages: number;
  /** 儲值 balance (HKD) when the brand has stored value, else null. */
  storedValue: number | null;
  /** Current 付費會籍, else null. */
  paidMembership: MemberPaidMembership | null;
}

/**
 * purchase / adjustment come from both sources; the rest only from the native engine:
 * redeem = points spent on a coupon, expire = yearly clearing, gift = welcome / birthday points,
 * stamps = stamps exchanged for a gift, tier = level changed (points 0, see `note`).
 */
/** spend = 積分抵扣 at a POS (POS 連接). */
export type PointsEntryKind = 'purchase' | 'adjustment' | 'redeem' | 'expire' | 'gift' | 'stamps' | 'tier' | 'spend';

export interface PointsEntry {
  date: string;
  shopName: string;
  invoiceNo: string | null;
  amount: number | null;
  points: number;
  /** Stamps added (+) or used (-); 0 for PROAN brands. */
  stamps: number;
  kind: PointsEntryKind;
  note: string | null;
}

export interface RewardPublic {
  id: string;
  couponType: CouponType;
  title: LocalizedText;
  description: LocalizedText;
  terms: LocalizedText;
  imageUrl: string | null;
  points: number;
  /** How long the coupon lasts once redeemed (native brands); null = set by the POS / no limit. */
  validity: { mode: 'fixed'; date: string } | { mode: 'days'; days: number } | null;
  redeemStart: string | null;
  redeemEnd: string | null;
  /** null = unlimited */
  remaining: number | null;
  perMemberLimit: number | null;
  redeemedByMe: number;
}

export interface MemberCoupon {
  id: string;
  rewardId: string | null;
  title: LocalizedText;
  imageUrl: string | null;
  terms: LocalizedText;
  validFrom: string | null;
  validTo: string | null;
  /** 可使用 / 已使用 / 已過期. Cancelled coupons are not listed. */
  status: 'available' | 'used' | 'expired';
  /** Native brands: the code staff scan or type to accept the coupon. */
  code: string | null;
  /** POS 連接 on: the QR the POS scans ("VC:…"); shown instead of `code`. */
  posQr: string | null;
}

export type RegisterResult =
  | { status: 'registered'; member: MemberSelf }
  /** The email already belongs to an in-store POS member; a code was emailed to prove ownership. */
  | { status: 'verify_existing'; email: string }
  /** A code was emailed (or one sent under a minute ago still waits); send the same form again with emailCode. */
  | { status: 'verify_email'; email: string; resendAfterSec: number };

/**
 * GET /api/public/auth/google/pending: a Google account that is not a member yet, to fill in the
 * sign-up form (/register?google=token). The email is the Google one and cannot be changed.
 */
export interface GooglePending {
  email: string;
  firstName: string;
  lastName: string;
}

/** ?google= on the login page after the Google step did not sign the visitor in. */
export const GOOGLE_LOGIN_ERRORS = ['cancelled', 'failed', 'expired', 'unavailable', 'unverified'] as const;
export type GoogleLoginError = (typeof GOOGLE_LOGIN_ERRORS)[number];

// ---------------------------------------------------------------- staff (admin portal + brand portal)

export type StaffRole = 'platform_admin' | 'brand_owner' | 'brand_staff';

export interface StaffSelf {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  brandId: string | null;
  totpEnabled: boolean;
  /** Platform admins: their role (null = created before roles, full rights). Brand users: null. */
  adminRole: AdminRole | null;
  /** Brand users limited to some shops (StoreInfo.id); null = every shop. */
  shopIds: string[] | null;
}

export interface PortalMe {
  user: StaffSelf;
  brand: { id: string; name: string; slug: string; languages: Lang[]; defaultLang: Lang; memberSiteUrl: string; dataSource: DataSource };
  /**
   * Set when a platform admin is signed in to the brand portal: every brand they can switch to
   * (POST /api/portal/auth/switch-brand). null for brand owners and staff.
   */
  adminBrands: Array<{ id: string; name: string; slug: string; status: BrandStatus }> | null;
  /** Plan, modules, limits and usage: hide pages of modules not in `entitlements.modules`. */
  entitlements: BrandEntitlements;
  /** Shops of the brand (for pickers); staff limited to some shops only get theirs. */
  stores: Array<{ id: string; name: LocalizedText }>;
  /** Platform admin without the brands.portalWrite permission: every change is refused. */
  readOnly: boolean;
}

// ---------------------------------------------------------------- brand portal

export interface ProgramSettings {
  /** Programme year, "MM-DD" (e.g. 01-01 to 12-31). Levels without a validity end on yearEnd. */
  yearStart: string;
  yearEnd: string;
  /** Every year on this "MM-DD" all points are cleared; null = points never expire. */
  pointsClearDate: string | null;
  /** `ratio`: what a sale earns, the same for every level (LPSS-21; it used to be set per level). */
  stamps: { enabled: boolean; clearDate: string | null; ratio: StampRatio | null };
  /** Gift on joining: a coupon (reward id) and/or extra points. */
  welcome: { enabled: boolean; rewardId: string | null; points: number };
  /** Gift on the birthday (day) or on the 1st of the birthday month (month). */
  birthday: { enabled: boolean; rewardId: string | null; points: number; timing: 'day' | 'month' };
}

export interface BrandSettings {
  profile: {
    name: LocalizedText;
    about: LocalizedText;
    address: LocalizedText;
    /** Google Maps share link of the main address. */
    mapUrl?: string;
    officeHours: LocalizedText;
    phone: string;
    email: string;
    socials: SocialLinks;
    stores: StoreInfo[];
    /** Brand photos (upload keys), at most 10. */
    galleryKeys?: string[];
  };
  theme: {
    template: SiteTemplate;
    colors: ThemeColors;
    logoKey: string | null;
    faviconKey: string | null;
    /** Picture beside the brand story on the member site. */
    storyImageKey?: string | null;
  };
  languages: { enabled: Lang[]; default: Lang };
  /** 主頁內容: home blocks and banner carousel speed. */
  home: HomeSettings;
  seo: { title: LocalizedText; description: LocalizedText; ogImageKey: string | null };
  email: {
    senderName: string;
    replyTo: string;
    welcomeSubject: LocalizedText;
    welcomeHeader: LocalizedText;
    welcomeBody: LocalizedText;
    welcomeFooter: LocalizedText;
  };
  registration: SiteConfig['registration'];
  legal: { terms: LocalizedText; privacy: LocalizedText; termsVersion: string };
  features: { news: boolean; rewards: boolean; wallet: boolean };
  consent: { channels: ConsentChannel[]; statement: LocalizedText };
  /** Points, stamps and gift rules of the platform's own programme (native brands). */
  program: ProgramSettings;
  /** 自動提醒 (automations module). */
  automations: AutomationSettings;
  /** 推薦獎賞 (referralRewards module). */
  referral: ReferralSettings;
  /** 儲值 (storedValue module). */
  storedValue: StoredValueSettings;
  /** 渠道追蹤. */
  tracking: TrackingSettings;
  /** POS 連接 options (native brands whose POS is connected). */
  pos: PosSettings;
  /** 會員編號 of new members (native brands; PROAN brands get theirs from the POS). */
  memberNo: MemberNoSettings;
}

/**
 * LPSS-45: a new member's 會員編號 is `prefix` + `next` (EMP + 100001 → EMP100001; no prefix = the number only),
 * then `next` goes up by 1. A number whose code a member already has is passed over. Existing members keep theirs.
 */
export interface MemberNoSettings {
  /** A–Z only, at most 6 letters; may be empty. */
  prefix: string;
  next: number;
}

/** 渠道追蹤: a click on a link counts for sign-ups within `windowHours`; a later click on another link replaces it. */
export interface TrackingSettings {
  windowHours: number;
}

export type SettingsSection = keyof BrandSettings;

export interface MediaRef {
  key: string;
  url: string;
}

export interface BannerAdmin {
  id: string;
  imageKey: string;
  imageUrl: string;
  mobileImageKey: string | null;
  mobileImageUrl: string | null;
  title: LocalizedText;
  link: string | null;
  startAt: string | null;
  endAt: string | null;
  sort: number;
  active: boolean;
}

export interface NewsCategoryAdmin {
  id: string;
  name: LocalizedText;
  sort: number;
  newsCount: number;
}

export interface NewsAdmin {
  id: string;
  categoryId: string | null;
  title: LocalizedText;
  summary: LocalizedText;
  body: LocalizedText;
  imageKey: string | null;
  imageUrl: string | null;
  publishStart: string | null;
  publishEnd: string | null;
  featured: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** PROAN brands: a level from the POS, with what the brand shows for it. Rules stay in the POS. */
export interface LevelAdmin {
  custTypeId: number;
  posCode: string;
  posName: string;
  display: {
    name: LocalizedText;
    color: string;
    benefits: LocalizedText;
    sort: number;
    cardImageKey: string | null;
    cardImageUrl: string | null;
    iconKey: string | null;
    iconUrl: string | null;
  } | null;
}

/** Native brands: a level of the platform's own programme, fully editable. */
export interface TierAdmin extends LevelRules {
  id: number;
  name: LocalizedText;
  description: LocalizedText;
  color: string;
  cardImageKey: string | null;
  cardImageUrl: string | null;
  iconKey: string | null;
  iconUrl: string | null;
  sort: number;
  /** New members start here; exactly one level is the entry level and it never expires. */
  isDefault: boolean;
  memberCount: number;
}

/** One reward on a stamp card (StampCardAdmin). */
export interface StampRewardAdmin {
  /** null = added in edits not published yet. */
  id: string | null;
  stamps: number;
  title: LocalizedText;
  /** Coupon given (from 優惠券), or null. */
  rewardId: string | null;
  /** Extra points given. */
  points: number;
  /** Times given to members (before LPSS-26: times exchanged). */
  usedCount: number;
}

/**
 * LPSS-35 / LPSS-36: one stamp card of 印花獎賞. The rewards and 有效日期 are as the editor opens them: the edits saved
 * with 儲存 when there are any, else the published card. Sales and members only use the published card.
 */
export interface StampCardAdmin {
  id: string;
  /** 已發佈: sales add stamps to it and members see it (until validTo). A new card starts 未發佈. */
  published: boolean;
  /** Edits saved with 儲存 and not published yet; members see the last published version until 發佈. */
  hasDraft: boolean;
  /** Published and past its last day: no more stamps or rewards on it. */
  expired: boolean;
  /** 有效日期: the last Hong Kong day the card works; null = 不會過期. */
  validTo: string | null;
  /** The reward that completes the card (the most stamps); null only for an old card without one. */
  final: StampRewardAdmin | null;
  /** 中途獎賞, fewest stamps first. */
  steps: StampRewardAdmin[];
  /** Rewards switched off before LPSS-33: not on the card and never given. */
  switchedOff: number;
  createdAt: string;
}

/** 'discount' is only kept for older clients; it is saved as a cash coupon with a percentage. */
export type CouponType = 'cash' | 'gift' | 'discount';
/** 公開兌換 = exchanged for points on the member site only; 只限派發 = only handed out (gifts, codes, staff); 可兌換及派發 = both. */
export type RewardNature = 'redeem' | 'issue' | 'both';
/** Cash coupons (native brands): 10 = 10% off, or $10 off. */
export type DiscountMode = 'percent' | 'amount';
/** redeemed = 可使用, fulfilled = 已使用, expired = 已過期, cancelled = hidden from the member. */
export type CouponStatus = 'redeemed' | 'fulfilled' | 'expired' | 'cancelled';
/** code = claimed from a code batch; public_code = 公開優惠碼; referral / automation / paid / campaign = gift of those features. */
export type CouponSource = 'points' | 'welcome' | 'birthday' | 'stamps' | 'manual' | 'code' | 'public_code' | 'referral' | 'automation' | 'paid' | 'campaign';

/** 優惠券派發記錄: one coupon handed to one member. */
export interface CouponIssueRow {
  id: string;
  createdAt: string;
  memberId: string;
  memberNo: string;
  memberName: string;
  rewardId: string;
  couponTitle: LocalizedText;
  source: CouponSource;
  /** Points spent (0 for gifts). */
  points: number;
  /** Native brands: code shown to the member and scanned by staff. */
  code: string | null;
  /** The 批量優惠碼 or 公開優惠碼 the member entered to get it. */
  viaCode?: string | null;
  validTo: string | null;
  status: CouponStatus;
  statusAt: string | null;
  statusBy: string | null;
}

/** 會員優惠 history: a welcome or birthday gift given. */
export interface GiftLogRow {
  id: string;
  createdAt: string;
  kind: 'welcome' | 'birthday';
  memberId: string;
  memberNo: string;
  memberName: string;
  couponTitle: LocalizedText | null;
  points: number;
}

/** Native brands: one line of a member's points / stamps account, as staff see it. */
export interface LedgerRow {
  id: string;
  createdAt: string;
  kind: PointsEntryKind;
  points: number;
  stamps: number;
  amount: number | null;
  invoiceNo: string | null;
  shop: string | null;
  note: string | null;
  actor: string | null;
}

/** Native brands: everything staff need at the counter for one member. */
export interface MemberLoyaltyDetail {
  member: MemberRow;
  level: { id: number; name: LocalizedText; color: string } | null;
  levelSince: string | null;
  /** Last day of the current level; null = does not expire. */
  levelExpires: string | null;
  points: number;
  /** The most stamps on one of stampCards (0 when none): what 調整 can take away. */
  stamps: number;
  /** LPSS-35: the published cards that have not expired, with the member's stamps on each (title = the final reward's). */
  stampCards: Array<{ id: string; title: LocalizedText; stamps: number }>;
  /** Spend counted since the current level started (for upgrade / keeping the level). */
  levelSpend: number;
  totalSpend: number;
  nextLevel: { id: number; name: LocalizedText; remainingAmount: number } | null;
  /** Coupons the member can still use. */
  coupons: CouponIssueRow[];
  ledger: LedgerRow[];
  /** 儲值 balance when the brand has stored value, else null. */
  storedValue: number | null;
  paidMembership: (MemberPaidMembership & { id: string }) | null;
  /** While a 付費會籍 is shown (`level`): the spend level underneath, which the member returns to when it ends. */
  normalLevel: { id: number; name: LocalizedText; color: string } | null;
}

/** 交易記錄 (native brands): one sale recorded in the brand portal. */
export interface TransactionRow {
  id: string;
  createdAt: string;
  memberId: string;
  memberNo: string;
  memberName: string;
  amount: number;
  points: number;
  stamps: number;
  invoiceNo: string | null;
  shop: string | null;
  /** StoreInfo.id when recorded with a shop from the list. */
  shopId: string | null;
  note: string | null;
  /** Staff email who recorded it. */
  actor: string | null;
  /** Products of the sale, as entered (may be empty). */
  lines: SaleLine[];
}

/** One product of a sale; the sale's amount stays the net total staff enter. */
export interface SaleLine {
  sku: string;
  quantity: number;
  /** Unit price (HKD), when entered. */
  price: number | null;
}

/** 交易記錄 page, with totals over every row matching the filter (not only this page). */
export interface TransactionPage extends Page<TransactionRow> {
  totalAmount: number;
  totalPoints: number;
}

/** Result of recording a sale for a native member. */
export interface PurchaseResult {
  points: number;
  stamps: number;
  /** Set when the sale moved the member up. */
  levelChanged: { from: LocalizedText | null; to: LocalizedText } | null;
  /** 推廣活動 that gave this sale something. */
  campaigns: AppliedCampaign[];
  /** Stamp rewards this sale's stamps reached (already given). */
  stampRewards: LocalizedText[];
  detail: MemberLoyaltyDetail;
}

export interface MemberRow {
  id: string;
  memberNo: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  /** Referral link code the member signed up through; null = came directly. */
  registerSource: string | null;
  marketingOptIn: boolean;
  status: 'active' | 'deletion_requested';
  createdAt: string;
  lastLoginAt: string | null;
  consents: Record<ConsentChannel, boolean>;
  /** Native brands only (PROAN brands: null, the POS holds them). */
  points: number | null;
  levelName: LocalizedText | null;
  /**
   * When the member's card went into Apple / Google Wallet; null = not in it now. Apple: a phone registered the card
   * for updates. Google: Google's save callback (cards saved before the callback was set up count from its first event).
   */
  wallet: { apple: string | null; google: string | null };
}

/** One change of a member's consent (or acceptance of a terms version), newest first. */
export interface ConsentLogRow {
  channel: ConsentChannel | 'terms';
  granted: boolean;
  /** register | profile | terms | deletion | unsubscribe (電子報 link) */
  source: string;
  termsVersion: string | null;
  at: string;
}

export interface WalletOverview {
  /** Brand switch (Settings > Features). */
  enabled: boolean;
  /** Platform credentials present. */
  platform: { apple: boolean; google: boolean };
  passes: { apple: number; google: number };
  /** Pass holders who allow wallet notifications. */
  reachable: number;
  /** Messages sent since 00:00 Hong Kong time. */
  sentToday: number;
  /** Notifications one card can get in 24 hours (WALLET_PER_CARD_DAILY). */
  perCardDaily: number;
  /** Messages waiting for their time. */
  scheduled: number;
}

export type WalletMessageStatus = 'scheduled' | 'sent' | 'cancelled' | 'failed';

/** off = switched off; scheduled = not started; running = on the cards now; ended = past its end. */
export type WalletNearbyStatus = 'off' | 'scheduled' | 'running' | 'ended';

export interface WalletNearbyAdmin {
  id: string;
  text: LocalizedText;
  shopIds: string[];
  startAt: string;
  endAt: string | null;
  active: boolean;
  status: WalletNearbyStatus;
  /** When the cards were last brought up to date with it (it started, or changed while running). */
  appliedAt: string | null;
  report: {
    /** Apple cards, and how many phones have fetched the card since appliedAt. */
    appleCards: number;
    appleUpdated: number;
    /** Google card class updated with the shop locations: ok / failed (with the reason) / null = not yet. */
    google: 'ok' | 'failed' | null;
    googleError: string | null;
  };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** What happened to a message's cards, per platform. Apple `updated` = phones that fetched the new card after the push. */
export interface WalletMessageReport {
  apple: { queued: number; sent: number; failed: number; updated: number };
  google: { queued: number; sent: number; failed: number };
}

export interface WalletMessageAdmin {
  id: string;
  title: LocalizedText;
  body: LocalizedText;
  status: WalletMessageStatus;
  /** When it goes (or went) out. */
  sendAt: string;
  sentAt: string | null;
  /** Cards the message was queued on (0 until it is sent). */
  recipients: number;
  /** Pass holders skipped because they did not allow wallet notifications. */
  skippedNoConsent: number;
  /** Cards skipped because they had already had WALLET_PER_CARD_DAILY notifications in the last 24 hours. */
  skippedLimit: number;
  /** Segment it was limited to, else null (everyone). */
  segmentId: string | null;
  segmentName: string | null;
  /** Why it could not be sent (status failed). */
  error: string | null;
  report: WalletMessageReport;
  createdBy: string;
  createdAt: string;
}

/** 渠道追蹤 link. Switched off or outside validFrom–validTo it still opens the site, but sign-ups are not counted for it. */
export interface ReferralLinkAdmin {
  code: string;
  name: string;
  /** Where it is shared, e.g. Instagram, 門市海報, KOL. */
  source: string;
  description: string;
  url: string;
  validFrom: string | null;
  validTo: string | null;
  clicks: number;
  signups: number;
  lastClickAt: string | null;
  active: boolean;
  createdAt: string;
}

/** One opening of a 渠道追蹤 link (點擊記錄). */
export interface LinkClickRow {
  id: string;
  createdAt: string;
  /** Site the visitor came from (host only), when the browser said. */
  referrer: string | null;
  device: 'mobile' | 'desktop' | null;
  /** false = the link was off or outside its valid period, so a sign-up is not counted for it. */
  counted: boolean;
  /** Set when this click led to a sign-up. */
  memberId: string | null;
  memberNo: string | null;
  memberName: string | null;
  registeredAt: string | null;
}

/** A member who joined through a 渠道追蹤 link. */
export interface LinkSignupRow {
  memberId: string;
  memberNo: string;
  memberName: string;
  joinedAt: string;
}

/** POST /api/public/ref-clicks: what a ?ref= code is (a 渠道追蹤 link, a member's referral code, or neither). */
export interface RefClickResult {
  kind: 'link' | 'member' | 'none';
}

export interface DeletionRequestAdmin {
  id: string;
  memberId: string;
  memberNo: string;
  email: string;
  name: string;
  reason: string;
  reasonText: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
}

export interface DailyStat {
  day: string;
  newMembers: number;
  logins: number;
  redemptions: number;
  walletAdds: number;
  emailsSent: number;
}

export interface BrandReport {
  from: string;
  to: string;
  totals: Omit<DailyStat, 'day'>;
  daily: DailyStat[];
  bySource: Array<{ source: string; count: number }>;
  totalMembers: number;
}

export interface PosCouponOption {
  type: 'cash' | 'gift';
  id: number;
  code: string;
  name: string;
}

/** 優惠券. PROAN brands: each maps to a POS coupon (`posCouponId`). Native brands: posCouponId is null. */
export interface RewardAdmin {
  id: string;
  couponType: CouponType;
  posCouponId: number | null;
  title: LocalizedText;
  description: LocalizedText;
  terms: LocalizedText;
  imageKey: string | null;
  imageUrl: string | null;
  /** Points to exchange on the member site (0 when not exchangeable). */
  points: number;
  /** Shown on the member site's exchange page (nature is not 'issue'); kept for older clients. */
  redeemable: boolean;
  nature: RewardNature;
  /** Native brands: coupon numbers are PREFIX00001, PREFIX00002…; null = random codes. */
  codePrefix: string | null;
  /** Native cash coupons: the value (10 = 10% off / $10 off); null = not set. */
  discountMode: DiscountMode | null;
  discountValue: number | null;
  /** 會員組別: levels (tier ids; POS level ids for PROAN brands) that may get it; null = every member. */
  tierIds: number[] | null;
  /** Native brands: how long a coupon lasts once received. */
  expiryMode: 'none' | 'fixed' | 'days';
  expiryDate: string | null;
  expiryDays: number | null;
  /** Exchange period. */
  redeemStart: string | null;
  redeemEnd: string | null;
  /** Stock size: coupons handed out + still available; null = unlimited. Changed through /rewards/:id/stock. */
  totalLimit: number | null;
  /** Coupons still available (totalLimit − redeemedCount); null = unlimited. */
  remaining: number | null;
  perMemberLimit: number | null;
  /** POS 連接: 適用菜式編號, comma separated; null = the whole bill. */
  posItemCodes: string | null;
  redeemedCount: number;
  /** Status: active = 啟用. */
  active: boolean;
  sort: number;
  createdAt: string;
}

/** 庫存記錄: one change of a coupon's stock. */
export interface StockLogRow {
  id: string;
  /** Coupons added (+) or taken away (−); null = made unlimited. */
  change: number | null;
  /** Coupons available after the change; null = unlimited. */
  stockAfter: number | null;
  note: string | null;
  actor: string;
  createdAt: string;
}

export interface StaffRow {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  status: 'invited' | 'active' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
  /** Platform admins only. */
  adminRole: AdminRole | null;
  /** Brand users: shops they are limited to; null = every shop. */
  shopIds: string[] | null;
  totpEnabled: boolean;
}

// ---------------------------------------------------------------- admin portal

export type BrandStatus = 'active' | 'suspended';

export interface BrandRow {
  id: string;
  slug: string;
  name: string;
  status: BrandStatus;
  dataSource: DataSource;
  contactName: string;
  contactEmail: string;
  contractStart: string | null;
  contractEnd: string | null;
  primaryDomain: string;
  memberCount: number;
  createdAt: string;
  planId: string | null;
  planName: string | null;
  /** Member limit of the plan (with the brand's override); null = none. */
  memberLimit: number | null;
}

export interface DomainRow {
  hostname: string;
  kind: 'subdomain' | 'custom';
  status: 'active' | 'pending' | 'error';
  /** What the merchant must add in their DNS for a custom domain. */
  dnsInstructions: string | null;
  createdAt: string;
}

export interface ProanConfigMasked {
  apiHost: string;
  hqApiHost: string | null;
  shopId: number | null;
  stationId: number | null;
  hasDesKey: boolean;
  hasCrmEncryptCode: boolean;
}

export interface BrandDetail extends BrandRow {
  contactTel: string | null;
  internalRemarks: string | null;
  logoKey: string | null;
  logoUrl: string | null;
  qrMode: QrMode;
  /** Member site address on the built-in subdomain. */
  memberSiteUrl: string;
  domains: DomainRow[];
  proan: ProanConfigMasked;
  users: StaffRow[];
  updatedAt: string;
  entitlements: BrandEntitlements;
}

export interface BrandHistoryRow {
  id: string;
  actorEmail: string | null;
  action: string;
  detail: Record<string, unknown>;
  createdAt: string;
}

/** 用戶: every account in the system (platform admins and brand users). */
export interface UserRow {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  status: 'invited' | 'active' | 'disabled';
  brandId: string | null;
  brandName: string | null;
  brandSlug: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  adminRole: AdminRole | null;
  totpEnabled: boolean;
}

/** "Log in to the merchant portal": a one-time link that signs the admin in to that brand. */
export interface PortalLoginLink {
  url: string;
  expiresAt: string;
}

export interface ProanTestResult {
  ok: boolean;
  latencyMs: number;
  message: string;
  levels?: Array<{ id: number; name: string }>;
}

export interface PlatformReport {
  from: string;
  to: string;
  brands: Array<{ brandId: string; brandName: string } & Omit<DailyStat, 'day'> & { totalMembers: number }>;
  daily: DailyStat[];
}
